import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AgencyHeader from '../../components/headers/AgencyHeader';
import { StatusConfirmationPopup } from '../../components/popups/AgencyDetailStopPop';
import AgencyInfoTab from '../../tabs/agencyDetail/AgencyInfoTab';
import AgencyInsuranceTab from '../../tabs/agencyDetail/AgencyInsuranceTab';
import AgencySolicitorsTab from '../../tabs/agencyDetail/AgencySolicitorsTab';
import { fetchAgencyData } from '../../services/api/AgencyService';
import { fetchInsuranceCompanies, fetchAgencyInsuranceCompanies } from '../../services/api/InsuranceService';
import { searchSolicitor, updateSolicitor } from '../../services/api/SolicitorService';
import '../../styles/Tabs.css';
import iconTitle from '../../assets/img/icon-square.svg';

// 代理店詳細ページコンポーネント
// ※pages/adminにも同様のコンポーネントがあるため、修正時は同時に確認すること
const AgencyDetail = () => {
  const { agencyId } = useParams();
  const navigate     = useNavigate();

  // ステート定義
  const [agencyData, setAgencyData] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [agencyInsuranceCompanies, setAgencyInsuranceCompanies] = useState([]);
  const [solicitors, setSolicitors] = useState([]);
  const [activeTab, setActiveTab] = useState('info');
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusConfirmationData, setStatusConfirmationData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.initialTab) {
      setActiveTab(location.state.initialTab);
    }
  }, [location.key, location.state?.initialTab]);

  // 初期データロード用のuseEffect
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!agencyId) {
          // agencyIdが未指定の場合のエラー表示
          setError('代理店IDが指定されていません。');
          return;
        }
        
        // 代理店データを取得（初期表示時はエラーダイアログを表示しない）
        const agencyResponse = await fetchAgencyData(agencyId, { 
          showErrorDialog: false 
        });
        setAgencyData(agencyResponse);

        // 保険会社データを取得
        handleDataUpdated();
        
        // 募集人データを取得
        const searchData = {
          agency_id: parseInt(agencyId, 10),
          target: null,
          search_word: null,
          is_active: null
        };
        const solicitorsResponse = await searchSolicitor(searchData);
        setSolicitors(solicitorsResponse);

      } catch (err) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      } finally {
        // ローディング終了
        setIsLoading(false);
      }
    };
    fetchData();
  }, [agencyId, navigate]);

  // 編集ボタン押下時
  const handleEdit = () => navigate(`/agency/edit/${agencyId}`);

const handleDataUpdated = async () => {
    try {
      // 保険会社データを取得
      const insuranceCompaniesResponse = await fetchInsuranceCompanies();

      // 代理店と保険会社の紐づけデータを取得
      const agencyInsuranceCompaniesResponse = await fetchAgencyInsuranceCompanies(agencyId);
      setAgencyInsuranceCompanies(agencyInsuranceCompaniesResponse);

      // 代理店に紐づいている保険会社のIDリストを作成
      const agencyCompanyIds = agencyInsuranceCompaniesResponse.map(company => company.id);
      
      // 紐づいている保険会社のみをフィルタリング
      const filteredInsuranceCompanies = insuranceCompaniesResponse.filter(company => 
        agencyCompanyIds.includes(company.id)
      );
      setInsuranceCompanies(filteredInsuranceCompanies);
    } catch (error) {
      console.error('データ更新エラー:', error);
    }
  };

  // 停止/再開ボタンクリック時のデータ設定
  const handleStatusButtonClick = solicitor => {
    console.log('handleStatusButtonClick called with solicitor:', solicitor);
    setStatusConfirmationData(solicitor);
    console.log(setStatusConfirmationData);
    setShowStatusConfirmation(true);
  };

  // 確認ポップアップを閉じる
  const closeStatusConfirmation = () => {
    setShowStatusConfirmation(false);
    setStatusConfirmationData(null);
  };

  // ステータス変更を確定
  const confirmStatusChange = () => {
    if (statusConfirmationData) {
      handleStatusToggle(statusConfirmationData.id);
    }
    closeStatusConfirmation();
  };

  // 募集人ステータス切り替え処理
  const handleStatusToggle = async solicitorId => {
    setIsLoading(true);
    try {
      const target = solicitors.find(s => s.id === solicitorId);
      if (!target) {
        throw new Error('募集人が見つかりません');
      }
      const newStatus = !target.is_active;
      await updateSolicitor(solicitorId, { is_active: newStatus });
      // 成功したらローカルステートを更新
      setSolicitors(
        solicitors.map(s =>
          s.id === solicitorId ? { ...s, is_active: newStatus } : s
        )
      );
    } catch (err) {
      // エラーダイアログ表示はBaseApiServiceで行う
      // エラーコンソール出力は各ApiServiceで行う
    } finally {
      setIsLoading(false);
    }
  };

  // ローディング中表示
  if (isLoading) return <div>Loading...</div>;

  // エラー発生、またはデータがない場合
  if (error || !agencyData) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={() => navigate(-1)}>前の画面に戻る</button>
      </div>
    );
  }

  // 通常画面描画
  return (
    <>
      <AgencyHeader />
      <div className="page-container">
        <div className="content-wrapper">
          <div className="page-header">
            <div className="title-section">
              <div className="icon-square">
                <img src={iconTitle} alt="タイトルアイコン" />
              </div>
              <h1>代理店詳細：{agencyData.name}</h1>
            </div>
          </div>
          <div className="tab-container">
            <div className="tab-menu">
              {['info', 'insurance', 'solicitors'].map(tab => (
                <button
                  key={tab}
                  className={`tab-button ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'info'
                    ? '代理店情報'
                    : tab === 'insurance'
                    ? '登録保険会社'
                    : '募集人'}
                </button>
              ))}
            </div>
            <div className="tab-content">
              {activeTab === 'info' && (
                <AgencyInfoTab
                  agencyData={agencyData}
                  onEdit={handleEdit}
                  onReturn={() => navigate(-1)}
                />
              )}
              {activeTab === 'insurance' && (
                <AgencyInsuranceTab
                  insuranceCompanies={insuranceCompanies}
                  onEdit={handleEdit}
                  onReturn={() => navigate(-1)}
                  onDataUpdated={handleDataUpdated}
                />
              )}
              {activeTab === 'solicitors' && (
                <AgencySolicitorsTab
                  agencyId={agencyId}
                  solicitors={solicitors}
                  onStatusChange={handleStatusButtonClick}
                  onReturn={() => navigate(-1)}
                  onAddSolicitor={() => navigate(`/agency/registration/${agencyId}`)}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* 停止/再開確認ポップアップ */}
      <StatusConfirmationPopup
        showConfirmationPopup={showStatusConfirmation}
        confirmationPopupData={statusConfirmationData}
        closeConfirmationPopup={closeStatusConfirmation}
        confirmStatusChange={confirmStatusChange}
      />
    </>
  );
};

export default AgencyDetail;